/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/url/AddQueryParameterToURL.ts
 * @create:      2025-06-28 09:09:34.989
 * @modify:      2026-03-12 14:06:53.817
 * @version:     6.1.1
 * @times:       6
 * @lines:       85
 * @copyright:   Copyright © 2018-2026 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

/**
 * Adds or replaces a query parameter in the given URL string.
 *
 * If the parameter already exists and `noReplace` is `false`, its value will be replaced.
 * If the parameter does not exist, it will be appended to the query string.
 * If `value` is `undefined`, the parameter will be added without a value (e.g., `?name`).
 * The function preserves any hash fragment present in the original URL.
 *
 * @param url - The original URL string to modify.
 * @param name - The name of the query parameter to add or replace.
 * @param value - The value of the query parameter. If omitted, the parameter is added without a value.
 * @param noReplace - If `true`, the function will not replace an existing parameter; it will append a new one instead. Defaults to `false`.
 * @returns The new URL string with the query parameter added or replaced.
 * 
 * @since 4.0.0
 * @version 2026-03-12
 */
export function AddQueryParameterToURL(url: string, name: string, value?: string, noReplace = false) {
    let newURL: string;
    
    // Using a positive lookahead (?=\=) to find the
    // given parameter, preceded by a ? or &, and followed
    // by a = with a value after than (using a non-greedy selector)
    // and then followed by a & or the end of the string
    const val = new RegExp("(\\?|\\&)" + name + "=.*?(?=(&|$))");
    const parts = url.toString().split("#");
    url = parts[0];
    const hash = parts[1];
    const queryString = /\?.+$/;

    const parameter = value === undefined ? name : name + "=" + value;

    // Check if the parameter exists
    if (!noReplace && val.test(url)) {
        // if it does, replace it, using the captured group
        // to determine & or ? at the beginning
        newURL = url.replace(val, "$1" + parameter);
    } else if (queryString.test(url)) {
        // otherwise, if there is a query string at all
        // add the param to the end of it
        newURL = url + "&" + parameter;
    } else {
        // if there's no query string, add one
        newURL = url + "?" + parameter;
    }

    if (hash) {
        newURL += "#" + hash;
    }

    return newURL;
}
